import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

const MechanicalProperties = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filters, setFilters] = useState({
    heat_no: '',
    test_date_from: '',
    test_date_to: ''
  });

  const [formData, setFormData] = useState({
    heat_no: '',
    test_date: new Date().toISOString().split('T')[0],
    tensile_strength: '',
    yield_strength: '',
    elongation: '',
    reduction_of_area: '',
    impact_value: '',
    hardness: '',
    test_temperature: '',
    test_specimen: '',
    remarks: ''
  });

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { api } = useAuth();

  // Fetch Mechanical Properties data
  const { data: mechanicalData, isLoading, error, refetch } = useQuery(
    ['mechanicalProperties', page, rowsPerPage, searchTerm, filters],
    async () => {
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...filters
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await api.get(`/raw-material/mechanical?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true,
      onError: (error) => {
        enqueueSnackbar(`Error fetching mechanical properties: ${error.message}`, { variant: 'error' });
      }
    }
  );

  // Create mutation
  const createMutation = useMutation(
    (data) => api.post('/raw-material/mechanical', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('mechanicalProperties');
        enqueueSnackbar('Mechanical properties record created successfully', { variant: 'success' });
        handleCloseDialog();
      },
      onError: (error) => {
        enqueueSnackbar(`Error creating record: ${error.response?.data?.message || error.message}`, { variant: 'error' });
      }
    }
  );

  // Update mutation
  const updateMutation = useMutation(
    ({ id, data }) => api.put(`/raw-material/mechanical/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('mechanicalProperties');
        enqueueSnackbar('Mechanical properties record updated successfully', { variant: 'success' });
        handleCloseDialog();
      },
      onError: (error) => {
        enqueueSnackbar(`Error updating record: ${error.response?.data?.message || error.message}`, { variant: 'error' });
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    (id) => api.delete(`/raw-material/mechanical/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('mechanicalProperties');
        enqueueSnackbar('Mechanical properties record deleted successfully', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error deleting record: ${error.response?.data?.message || error.message}`, { variant: 'error' });
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      tensile_strength: formData.tensile_strength ? parseFloat(formData.tensile_strength) : null,
      yield_strength: formData.yield_strength ? parseFloat(formData.yield_strength) : null,
      elongation: formData.elongation ? parseFloat(formData.elongation) : null,
      reduction_of_area: formData.reduction_of_area ? parseFloat(formData.reduction_of_area) : null,
      impact_value: formData.impact_value ? parseFloat(formData.impact_value) : null,
      hardness: formData.hardness ? parseFloat(formData.hardness) : null,
      test_temperature: formData.test_temperature ? parseInt(formData.test_temperature) : null
    };

    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      heat_no: item.heat_no || '',
      test_date: item.test_date || '',
      tensile_strength: item.tensile_strength || '',
      yield_strength: item.yield_strength || '',
      elongation: item.elongation || '',
      reduction_of_area: item.reduction_of_area || '',
      impact_value: item.impact_value || '',
      hardness: item.hardness || '',
      test_temperature: item.test_temperature || '',
      test_specimen: item.test_specimen || '',
      remarks: item.remarks || ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData({
      heat_no: '',
      test_date: new Date().toISOString().split('T')[0],
      tensile_strength: '',
      yield_strength: '',
      elongation: '',
      reduction_of_area: '',
      impact_value: '',
      hardness: '',
      test_temperature: '',
      test_specimen: '',
      remarks: ''
    });
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this mechanical properties record?')) {
      deleteMutation.mutate(id);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading mechanical properties data: {error.message}
        </Alert>
      </Box>
    );
  }

  const mechanicalProperties = mechanicalData?.data?.mechanical || [];
  const totalCount = mechanicalData?.data?.pagination?.total || 0;

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Mechanical Properties Testing
      </Typography>

      {/* Filter Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by heat no, specimen..."
              InputProps={{
                endAdornment: <SearchIcon />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Heat No"
              value={filters.heat_no}
              onChange={(e) => setFilters({ ...filters, heat_no: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={filters.test_date_from}
              onChange={(e) => setFilters({ ...filters, test_date_from: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={filters.test_date_to}
              onChange={(e) => setFilters({ ...filters, test_date_to: e.target.value })}
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={() => refetch()}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box mb={2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add Mechanical Test
        </Button>
      </Box>

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Test Date</TableCell>
              <TableCell>Heat No</TableCell>
              <TableCell>Tensile Strength</TableCell>
              <TableCell>Yield Strength</TableCell>
              <TableCell>Elongation (%)</TableCell>
              <TableCell>Impact Value</TableCell>
              <TableCell>Hardness</TableCell>
              <TableCell>Test Specimen</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : mechanicalProperties.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No mechanical properties records found
                </TableCell>
              </TableRow>
            ) : (
              mechanicalProperties.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{formatDate(item.test_date)}</TableCell>
                  <TableCell>{item.heat_no}</TableCell>
                  <TableCell>{item.tensile_strength}</TableCell>
                  <TableCell>{item.yield_strength}</TableCell>
                  <TableCell>{item.elongation}</TableCell>
                  <TableCell>{item.impact_value}</TableCell>
                  <TableCell>{item.hardness}</TableCell>
                  <TableCell>{item.test_specimen}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEdit(item)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingItem ? 'Edit Mechanical Properties' : 'Add New Mechanical Test'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Heat Number"
                  value={formData.heat_no}
                  onChange={(e) => setFormData({ ...formData, heat_no: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Test Date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.test_date}
                  onChange={(e) => setFormData({ ...formData, test_date: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Tensile Strength (MPa)"
                  value={formData.tensile_strength}
                  onChange={(e) => setFormData({ ...formData, tensile_strength: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Yield Strength (MPa)"
                  value={formData.yield_strength}
                  onChange={(e) => setFormData({ ...formData, yield_strength: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  step="0.1"
                  label="Elongation (%)"
                  value={formData.elongation}
                  onChange={(e) => setFormData({ ...formData, elongation: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  step="0.1"
                  label="Reduction of Area (%)"
                  value={formData.reduction_of_area}
                  onChange={(e) => setFormData({ ...formData, reduction_of_area: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Impact Value (J)"
                  value={formData.impact_value}
                  onChange={(e) => setFormData({ ...formData, impact_value: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Hardness (HRC/HB)"
                  value={formData.hardness}
                  onChange={(e) => setFormData({ ...formData, hardness: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Test Temperature (°C)"
                  value={formData.test_temperature}
                  onChange={(e) => setFormData({ ...formData, test_temperature: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Test Specimen"
                  value={formData.test_specimen}
                  onChange={(e) => setFormData({ ...formData, test_specimen: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Remarks"
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              {createMutation.isLoading || updateMutation.isLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default MechanicalProperties;